import { Auth0Provider, type AppState } from '@auth0/auth0-react';
import type { ReactNode } from 'react';
import { router } from '@/router';

/**
 * Wraps the app in Auth0's SPA provider (PKCE + refresh-token rotation).
 *
 * `useRefreshTokens` + the `offline_access` scope mint a rotating refresh token
 * so `getAccessTokenSilently()` can renew the short-lived access token without
 * an iframe/third-party-cookie round trip. `cacheLocation="localstorage"` keeps
 * that refresh token across reloads and tabs (the trade-off is XSS exposure,
 * bounded by rotation + a short access-token TTL). Replaces the old server-side
 * `remix-auth` Auth0 code flow + httpOnly session cookie.
 */
// Integration OAuth callbacks land on these paths with their own `code`/`state`
// query params. The Auth0 SDK auto-detects `code`+`state` on load and would try
// to exchange them as if they were an Auth0 login — corrupting the session. Tell
// it to skip its callback handling on these paths and just restore the session
// from cache; our own callback pages exchange the provider code.
const INTEGRATION_CALLBACK_PATHS = ['/fundment/callback', '/addepar/callback'];

export function Auth0ProviderWithConfig({ children }: { children: ReactNode }) {
    const onRedirectCallback = (appState?: AppState) => {
        router.navigate(appState?.returnTo ?? '/');
    };

    const skipRedirectCallback = INTEGRATION_CALLBACK_PATHS.includes(
        window.location.pathname
    );

    return (
        <Auth0Provider
            domain={import.meta.env.VITE_AUTH0_DOMAIN}
            clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
            useRefreshTokens
            cacheLocation="localstorage"
            skipRedirectCallback={skipRedirectCallback}
            onRedirectCallback={onRedirectCallback}
            authorizationParams={{
                redirect_uri: window.location.origin,
                audience: import.meta.env.VITE_AUTH0_AUDIENCE,
                // `offline_access` is required for Auth0 to issue a refresh
                // token — without it `useRefreshTokens` silently falls back to
                // the (browser-blocked) iframe silent-auth path.
                scope: 'openid profile email offline_access',
                // Mirrors the old EtchoAuth0Strategy.authorizationParams which
                // forced a specific connection. Omitted if env var is unset.
                ...(import.meta.env.VITE_AUTH0_CONNECTION
                    ? { connection: import.meta.env.VITE_AUTH0_CONNECTION }
                    : {}),
            }}
        >
            {children}
        </Auth0Provider>
    );
}
